import { useCallback, useEffect, useRef, useState } from 'react';
import type { EventView, EntryView } from '@koto-event/shared';
import { useDeadlineText } from '../lib/deadline.js';
import { ApiError } from '../lib/api.js';
import { reauthenticate } from '../lib/liff.js';

interface Props {
  event: EventView;
  entry: EntryView;
  addFriendUrl: string;
  onAddFriend: () => void;
  /** 友だち登録の確認（フロー図「登録を確認」） */
  onCheckFriend: () => Promise<EntryView>;
  /** 友だち確認済みで、スタッフ提示画面へ進む（フロー図「特典を受け取る」） */
  onProceed: () => void;
  onUpdated: (entry: EntryView) => void;
}

/**
 * 抽選結果（仮当選）画面。
 *
 * フロー図の分岐に対応:
 *   登録済み  → 「特典を受け取る」でスタッフ提示画面へ
 *   未登録    → 「LINEで友だち追加」→ 戻ってきたら「登録を確認」
 *
 * 当選は確定ではなく仮状態。電子スタンプの押印で確定し、特典が付与される。
 */
export function ResultScreen({
  event,
  entry,
  addFriendUrl,
  onAddFriend,
  onCheckFriend,
  onProceed,
  onUpdated,
}: Props): JSX.Element {
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [authenticationExpired, setAuthenticationExpired] = useState(false);
  // 友だち追加画面へ一度でも遷移したら「登録を確認」を出す
  const [returnedFromLine, setReturnedFromLine] = useState(false);

  const remaining = useDeadlineText(entry.expiresAt);
  const checkingRef = useRef(false);

  const needsFriend = event.requireFriendForReward && !entry.isFriend;

  const handleCheck = useCallback(async (): Promise<void> => {
    if (checkingRef.current || authenticationExpired) return;
    checkingRef.current = true;
    setChecking(true);
    setMessage(null);
    try {
      const updated = await onCheckFriend();
      onUpdated(updated);
      if (!updated.isFriend) {
        setMessage('友だち登録が確認できませんでした。登録後にもう一度お試しください。');
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setAuthenticationExpired(true);
      setMessage(e instanceof Error ? e.message : '確認に失敗しました');
    } finally {
      checkingRef.current = false;
      setChecking(false);
    }
  }, [onCheckFriend, onUpdated, authenticationExpired]);

  // LINE の友だち追加から戻ってきたタイミングで、操作を待たずに確認する
  useEffect(() => {
    if (!needsFriend || !returnedFromLine) return;
    const onVisible = (): void => {
      if (!document.hidden) void handleCheck();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [needsFriend, returnedFromLine, handleCheck]);

  return (
    <div className="stack">
      <div className="card center">
        {event.screens.resultImageUrl && (
          <img className="hero-image" src={event.screens.resultImageUrl} alt="" />
        )}
        <h1>{entry.prize ? `${entry.prize.name} 当選！` : 'ざんねん…'}</h1>
        {entry.prize?.imageUrl && <img className="hero-image" src={entry.prize.imageUrl} alt="" />}
        {entry.prize && <p>{entry.prize.description}</p>}
        {entry.prize && event.screens.resultBody && <p>{event.screens.resultBody}</p>}
        {/* ハズレ（参加賞もない）は押印の対象外なので、仮当選とは表示しない */}
        {entry.prize && <span className="badge">抽選結果 仮当選</span>}
        {entry.prize && remaining && <p className="muted">受取期限: {remaining}</p>}
      </div>

      {!entry.prize ? (
        <div className="card center">
          <p>またのご参加をお待ちしております。</p>
        </div>
      ) : needsFriend ? (
        <div className="card">
          <p>
            特典を受け取るには公式LINEの友だち登録が必要です。
          </p>
          <div className="stack">
            {authenticationExpired ? (
              <button type="button" className="btn" onClick={reauthenticate}>LINE認証をやり直す</button>
            ) : !returnedFromLine ? (
              <button
                type="button"
                className="btn btn--line"
                onClick={() => {
                  setReturnedFromLine(true);
                  onAddFriend();
                }}
                disabled={!addFriendUrl}
              >
                LINEで友だち追加
              </button>
            ) : (
              <button type="button" className="btn" onClick={() => void handleCheck()} disabled={checking}>
                {checking ? '確認中…' : '登録を確認'}
              </button>
            )}
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setReturnedFromLine((v) => !v)}
            >
              {returnedFromLine ? '友だち追加に戻る' : '登録済みの方はこちら'}
            </button>
          </div>
          {message && <p className="error">{message}</p>}
        </div>
      ) : (
        <button type="button" className="btn" onClick={onProceed}>
          特典を受け取る
        </button>
      )}
    </div>
  );
}
